import { db } from '../config/firebase';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    query,
    where,
    getDocs,
    onSnapshot,
    serverTimestamp,
    setDoc,
    Timestamp,
    orderBy,
    limit,
} from 'firebase/firestore';

// ─── Phase 8: type-integer → NotificationType mapping ───────────────────────
// Mirrors NOTIFICATION_CATEGORY_TO_TYPE in Cloud Functions.
// Used to decode `type` (int) in `users/{uid}/notifications` subcollection docs.
const CF_TYPE_INT_TO_WEB_TYPE: Record<number, string> = {
    0: 'document_expiry',
    1: 'accommodation_cancellation',
    2: 'booking_imported',
    3: 'system',
    4: 'system',
    5: 'family_invitation',
    6: 'upgrade_approved',
    7: 'upgrade_denied',
    8: 'tier_expiring_soon',
    9: 'tier_expired',
    10: 'user_blocked',
    11: 'upgrade_requested',
    12: 'subscription_activated',
    13: 'payment_received',
    14: 'payment_failed',
    15: 'subscription_cancelled',
    16: 'subscription_reactivated',
    17: 'subscription_paused',
    18: 'credits_added',
    19: 'trial_started',
    20: 'trial_ending',
    21: 'access_restored',
    22: 'downgrade_trips_locked',
    // Trip Market
    23: 'market_submission_pending',
    24: 'market_listing_approved',
    25: 'market_listing_rejected',
};

/** Maps CF `category` string to the web NotificationType string (fallback path). */
const CF_CATEGORY_TO_WEB_TYPE: Record<string, string> = {
    subscription_activated: 'subscription_activated',
    subscription_renewal: 'payment_received',
    subscription_issue: 'payment_failed',
    subscription_canceled: 'subscription_cancelled',
    upgrade_reactivated: 'subscription_reactivated',
    subscription_paused: 'subscription_paused',
    credits_added: 'credits_added',
    trial_started: 'trial_started',
    trial_ending: 'trial_ending',
    access_restored: 'access_restored',
    downgrade_locked: 'downgrade_trips_locked',
    subscription_expired: 'tier_expired',
    subscription_expiring: 'tier_expiring_soon',
    market_submission_pending: 'market_submission_pending',
    market_listing_approved: 'market_listing_approved',
    market_listing_rejected: 'market_listing_rejected',
};

export interface Notification {
    id?: string;
    userId: string;
    type: NotificationType;
    title: string;
    message: string;
    read: boolean;
    /** Present only on CF-written subscription notifications (mapped from `dismissed`). */
    dismissed?: boolean;
    actionUrl?: string;
    /**
     * Paddle-hosted invoice PDF URL — present on PAYMENT_RECEIVED and
     * CREDITS_ADDED notifications for Paddle web transactions.
     * Null / absent for Apple / Google transactions.
     */
    invoiceUrl?: string;
    /** Stable key used to prevent duplicate notifications (e.g. 'accommodation_abc_16000000'). */
    dedupeKey?: string;
    metadata?: {
        tripId?: string;
        tripName?: string;
        expenseId?: string;
        activityId?: string;
        bookingId?: string;
        accommodationId?: string;
        [key: string]: any;
    };
    createdAt: Date;
}

export enum NotificationType {
    TRIP_INVITE = 'trip_invite',
    TRIP_SHARED = 'trip_shared',
    BOOKING_IMPORTED = 'booking_imported',
    EXPENSE_ADDED = 'expense_added',
    ACTIVITY_REMINDER = 'activity_reminder',
    FLIGHT_UPDATE = 'flight_update',
    CAR_RENTAL_REMINDER = 'car_rental_reminder',
    TRIP_STARTING_SOON = 'trip_starting_soon',
    AI_SUGGESTION = 'ai_suggestion',
    ACCOMMODATION_CANCELLATION = 'accommodation_cancellation',
    DOCUMENT_EXPIRY = 'document_expiry',
    SYSTEM = 'system',
    FAMILY_INVITATION = 'family_invitation',
    // Phase 7 — admin & tier notifications
    UPGRADE_APPROVED = 'upgrade_approved',
    UPGRADE_DENIED = 'upgrade_denied',
    TIER_EXPIRING_SOON = 'tier_expiring_soon',
    TIER_EXPIRED = 'tier_expired',
    USER_BLOCKED = 'user_blocked',
    UPGRADE_REQUESTED = 'upgrade_requested',
    // Phase 8 — subscription payment notifications
    // String values align with CF category names + CF_TYPE_INT_TO_WEB_TYPE map.
    SUBSCRIPTION_ACTIVATED = 'subscription_activated',   // RC INITIAL_PURCHASE (non-trial)
    PAYMENT_RECEIVED = 'payment_received',         // RC RENEWAL
    PAYMENT_FAILED = 'payment_failed',           // RC BILLING_ISSUE
    SUBSCRIPTION_CANCELLED = 'subscription_cancelled',   // RC CANCELLATION
    SUBSCRIPTION_REACTIVATED = 'subscription_reactivated', // RC UNCANCELLATION
    SUBSCRIPTION_PAUSED = 'subscription_paused',      // RC SUBSCRIPTION_PAUSED
    CREDITS_ADDED = 'credits_added',            // RC NON_SUBSCRIPTION_PURCHASE
    TRIAL_STARTED = 'trial_started',            // RC INITIAL_PURCHASE (TRIAL)
    TRIAL_ENDING = 'trial_ending',             // checkTrialReminders day-6
    ACCESS_RESTORED = 'access_restored',          // Re-upgrade unlocks trips
    DOWNGRADE_TRIPS_LOCKED = 'downgrade_trips_locked',   // Expiration locked trips
    // Trip Market — written by the market Cloud Functions
    MARKET_SUBMISSION_PENDING = 'market_submission_pending', // Admin: awaiting review
    MARKET_LISTING_APPROVED = 'market_listing_approved',   // Owner: now published
    MARKET_LISTING_REJECTED = 'market_listing_rejected',   // Owner: declined
}

/**
 * Notification document as written by Cloud Functions to
 * `users/{uid}/notifications/{id}`.  The schema differs from the web-native
 * flat `Notification` shape: `body` instead of `message`, `isRead` instead of
 * `read`, `type` as int, `actionData` object instead of `actionUrl` string.
 * `adaptCFNotification()` converts one into the other.
 */
export interface CFNotification {
    id: string;
    userId?: string;
    /** Flutter NotificationType.index integer. */
    type: number;
    /** Cloud Function category string (e.g. 'subscription_renewal'). */
    category?: string;
    title: string;
    /** Body text (CF convention); maps to `message` in the web Notification interface. */
    body: string;
    /** Unix milliseconds timestamp. */
    timestamp: number;
    isRead: boolean;
    dismissed: boolean;
    data?: Record<string, string>;
    actionData?: {
        deepLinkTarget?: string;
        invoiceUrl?: string;
        [key: string]: string | undefined;
    };
    createdAt?: Timestamp;
}

/**
 * Adapts a Cloud Function-written `CFNotification` document to the web
 * `Notification` interface so the existing React UI can render it without
 * modification.
 */
function adaptCFNotification(cfDoc: CFNotification): Notification {
    // Resolve type string: prefer int map, fall back to category map
    const cfTypeStr =
        CF_TYPE_INT_TO_WEB_TYPE[cfDoc.type] ??
        (cfDoc.category ? CF_CATEGORY_TO_WEB_TYPE[cfDoc.category] : undefined) ??
        NotificationType.SYSTEM;

    const deepLinkTarget = cfDoc.actionData?.deepLinkTarget;
    const invoiceUrl = cfDoc.actionData?.invoiceUrl;

    // Map deepLinkTarget → actionUrl path so existing navigation logic works
    const actionUrl = deepLinkTarget
        ? deepLinkTargetToUrl(deepLinkTarget, cfDoc.data)
        : undefined;

    return {
        id: cfDoc.id,
        userId: cfDoc.userId ?? '',
        type: cfTypeStr as NotificationType,
        title: cfDoc.title,
        message: cfDoc.body,
        read: cfDoc.isRead,
        dismissed: cfDoc.dismissed,
        actionUrl,
        invoiceUrl: invoiceUrl ?? undefined,
        metadata: cfDoc.data ? { ...cfDoc.data } : undefined,
        createdAt:
            cfDoc.timestamp != null
                ? new Date(cfDoc.timestamp)
                : cfDoc.createdAt instanceof Timestamp
                    ? cfDoc.createdAt.toDate()
                    : new Date(),
    };
}

/**
 * Converts a `deepLinkTarget` constant to a web route path.
 *
 * [data] is the notification's `data` payload — some targets (a specific trip)
 * can only be resolved with a value from it.
 */
function deepLinkTargetToUrl(
    target: string,
    data?: Record<string, string>
): string {
    switch (target) {
        case 'subscription_screen':
        case 'subscription_screen_credits':
            return '/settings?tab=subscription';
        case 'manage_subscription':
            return '/settings?tab=subscription&action=manage';
        case 'trip_list':
            return '/trips';
        // Admin-only: the Trip Market review queue (a submission is pending).
        case 'admin_market':
            return '/admin?tab=market';
        // Market review verdict: land the owner on the trip it concerns.
        case 'trip_detail':
            return data?.tripId ? `/trips/${data.tripId}` : '/trips';
        default:
            return '/settings';
    }
}

export class NotificationService {
    private static readonly COLLECTION = 'notifications';

    // Check if a notification with a given dedupeKey already exists (prevents duplicates)
    static async notificationExists(userId: string, dedupeKey: string): Promise<boolean> {
        try {
            const q = query(
                collection(db, this.COLLECTION),
                where('userId', '==', userId),
                where('dedupeKey', '==', dedupeKey),
                limit(1)
            );
            const snap = await getDocs(q);
            return !snap.empty;
        } catch (error) {
            console.error('Error checking notification existence:', error);
            return false;
        }
    }

    // Create a new notification
    static async createNotification(
        notification: Omit<Notification, 'id' | 'createdAt'>
    ): Promise<string> {
        try {
            const docRef = await addDoc(collection(db, this.COLLECTION), {
                ...notification,
                createdAt: serverTimestamp(),
            });

            console.log('✅ Notification created:', docRef.id);
            return docRef.id;
        } catch (error) {
            console.error('Error creating notification:', error);
            throw error;
        }
    }

    // Mark notification as read
    static async markAsRead(notificationId: string): Promise<void> {
        try {
            const notificationRef = doc(db, this.COLLECTION, notificationId);
            await updateDoc(notificationRef, {
                read: true,
            });
            console.log('✅ Notification marked as read:', notificationId);
        } catch (error) {
            console.error('Error marking notification as read:', error);
            throw error;
        }
    }

    // Mark all notifications as read for a user
    static async markAllAsRead(userId: string): Promise<void> {
        try {
            const q = query(
                collection(db, this.COLLECTION),
                where('userId', '==', userId),
                where('read', '==', false)
            );

            const snapshot = await getDocs(q);
            const updatePromises = snapshot.docs.map((docSnapshot) =>
                updateDoc(doc(db, this.COLLECTION, docSnapshot.id), {
                    read: true,
                })
            );

            await Promise.all(updatePromises);
            console.log('✅ All notifications marked as read');
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            throw error;
        }
    }

    // Delete a notification
    static async deleteNotification(notificationId: string): Promise<void> {
        try {
            await deleteDoc(doc(db, this.COLLECTION, notificationId));
            console.log('✅ Notification deleted:', notificationId);
        } catch (error) {
            console.error('Error deleting notification:', error);
            throw error;
        }
    }

    /**
     * Delete all TRIP_INVITE notifications for a specific user + trip combo.
     * Called when a user is removed from a trip so stale bell-icon entries are cleaned up.
     */
    static async deleteTripInviteNotificationsForUser(userId: string, tripId: string): Promise<void> {
        try {
            const q = query(
                collection(db, this.COLLECTION),
                where('userId', '==', userId),
                where('type', '==', NotificationType.TRIP_INVITE)
            );
            const snapshot = await getDocs(q);
            const toDelete = snapshot.docs.filter((d) => d.data().metadata?.tripId === tripId);
            await Promise.all(toDelete.map((d) => deleteDoc(d.ref)));
        } catch (error) {
            console.error('Error deleting trip invite notifications:', error);
        }
    }

    // Subscribe to user notifications (real-time) — reads the flat `notifications` collection
    // written by web-native code.
    static subscribeToNotifications(
        userId: string,
        callback: (notifications: Notification[]) => void,
        maxResults: number = 50
    ): () => void {
        const q = query(
            collection(db, this.COLLECTION),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(maxResults)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const notifications = snapshot.docs.map((document) => {
                const data = document.data();
                return {
                    id: document.id,
                    ...data,
                    createdAt:
                        data.createdAt instanceof Timestamp
                            ? data.createdAt.toDate()
                            : new Date(data.createdAt),
                } as Notification;
            });

            callback(notifications);
        }, (error) => {
            console.error('Error in notifications subscription (flat):', error);
        });

        return unsubscribe;
    }

    /**
     * Subscribe to subscription notifications written by Cloud Functions to
     * `users/{uid}/notifications` (subcollection path used by both Flutter
     * and all Phase 8 Cloud Functions).
     *
     * CF documents use a different schema (`body`, `isRead`, `type` as int,
     * `actionData`) — they are adapted to the standard web `Notification`
     * interface by `adaptCFNotification()` so existing React UI works unchanged.
     *
     * Only returns non-dismissed notifications, sorted newest-first.
     */
    static subscribeToSubscriptionNotifications(
        userId: string,
        callback: (notifications: Notification[]) => void,
        maxResults: number = 50
    ): () => void {
        const q = query(
            collection(db, 'users', userId, 'notifications'),
            where('dismissed', '==', false),
            orderBy('createdAt', 'desc'),
            limit(maxResults)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const notifications = snapshot.docs
                .map((document) => {
                    const data = document.data() as Omit<CFNotification, 'id'>;
                    return adaptCFNotification({ id: document.id, ...data });
                })
                // Additional client-side guard — CF query uses `dismissed==false`
                // but a race-condition update could still slip through.
                .filter((n) => !n.dismissed);

            callback(notifications);
        }, (error) => {
            console.error('Error in subscription notifications listener:', error);
        });

        return unsubscribe;
    }

    /**
     * Dismiss a subscription notification in the `users/{uid}/notifications`
     * subcollection (sets `dismissed: true`, matching Flutter + CF convention).
     */
    static async dismissSubscriptionNotification(
        userId: string,
        notificationId: string
    ): Promise<void> {
        try {
            await updateDoc(
                doc(db, 'users', userId, 'notifications', notificationId),
                { dismissed: true }
            );
        } catch (error) {
            console.error('Error dismissing subscription notification:', error);
            throw error;
        }
    }

    /**
     * Mark a subscription notification (in the user subcollection) as read.
     */
    static async markSubscriptionNotificationAsRead(
        userId: string,
        notificationId: string
    ): Promise<void> {
        try {
            await updateDoc(
                doc(db, 'users', userId, 'notifications', notificationId),
                { isRead: true }
            );
        } catch (error) {
            console.error('Error marking subscription notification as read:', error);
            throw error;
        }
    }

    /**
     * Unread count for subscription notifications in the user subcollection.
     * Added to the count returned by `getUnreadCount()` for the full badge total.
     */
    static async getSubscriptionUnreadCount(userId: string): Promise<number> {
        try {
            const q = query(
                collection(db, 'users', userId, 'notifications'),
                where('dismissed', '==', false),
                where('isRead', '==', false)
            );
            const snap = await getDocs(q);
            return snap.size;
        } catch (error) {
            console.error('Error getting subscription unread count:', error);
            return 0;
        }
    }

    // Get unread count
    static async getUnreadCount(userId: string): Promise<number> {
        try {
            const q = query(
                collection(db, this.COLLECTION),
                where('userId', '==', userId),
                where('read', '==', false)
            );

            const snapshot = await getDocs(q);
            return snapshot.size;
        } catch (error) {
            console.error('Error getting unread count:', error);
            return 0;
        }
    }

    // Helper: Send trip invite notification
    static async sendTripInviteNotification(
        userId: string,
        tripId: string,
        tripName: string,
        inviterName: string
    ): Promise<string> {
        return this.createNotification({
            userId,
            type: NotificationType.TRIP_INVITE,
            title: 'Trip Invitation',
            message: `${inviterName} invited you to join "${tripName}"`,
            read: false,
            actionUrl: `/trips/${tripId}`,
            metadata: { tripId, tripName },
        });
    }

    // Helper: Send booking imported notification
    static async sendBookingImportedNotification(
        userId: string,
        bookingCount: number
    ): Promise<string> {
        return this.createNotification({
            userId,
            type: NotificationType.BOOKING_IMPORTED,
            title: 'Bookings Imported',
            message: `${bookingCount} new ${bookingCount === 1 ? 'booking' : 'bookings'
                } imported from your email`,
            read: false,
            actionUrl: '/bookings',
        });
    }

    // Helper: Send trip starting soon notification
    static async sendTripStartingSoonNotification(
        userId: string,
        tripId: string,
        tripName: string,
        daysUntil: number
    ): Promise<string> {
        return this.createNotification({
            userId,
            type: NotificationType.TRIP_STARTING_SOON,
            title: 'Trip Starting Soon!',
            message: `Your trip "${tripName}" starts in ${daysUntil} ${daysUntil === 1 ? 'day' : 'days'
                }`,
            read: false,
            actionUrl: `/trips/${tripId}`,
            metadata: { tripId, tripName },
        });
    }

    // Helper: Send family invitation notification
    static async sendFamilyInvitationNotification(
        inviteeUserId: string,
        invitationId: string,
        groupId: string,
        inviterName: string
    ): Promise<string> {
        return this.createNotification({
            userId: inviteeUserId,
            type: NotificationType.FAMILY_INVITATION,
            title: 'Family Plan Invitation',
            message: `${inviterName} invited you to join their Family Plan`,
            read: false,
            actionUrl: '/family',
            metadata: { invitationId, familyGroupId: groupId },
        });
    }

    // Helper: Send AI suggestion notification
    static async sendAISuggestionNotification(
        userId: string,
        tripId: string,
        tripName: string
    ): Promise<string> {
        return this.createNotification({
            userId,
            type: NotificationType.AI_SUGGESTION,
            title: 'AI Trip Suggestions Available',
            message: `New AI suggestions are ready for "${tripName}"`,
            read: false,
            actionUrl: `/trips/${tripId}`,
            metadata: { tripId, tripName },
        });
    }

    // Helper: Notify user their upgrade request was approved
    static async sendUpgradeApprovedNotification(
        userId: string,
        tierName: string
    ): Promise<string> {
        return this.createNotification({
            userId,
            type: NotificationType.UPGRADE_APPROVED,
            title: 'Upgrade Approved! 🎉',
            message: `Your request to upgrade to the ${tierName} plan has been approved. Enjoy your new features!`,
            read: false,
            actionUrl: '/settings',
            metadata: { tierName },
        });
    }

    // Helper: Notify user their upgrade request was denied
    static async sendUpgradeDeniedNotification(
        userId: string,
        reason?: string
    ): Promise<string> {
        const message = reason
            ? `Your upgrade request was not approved. Reason: ${reason}`
            : 'Your upgrade request was not approved at this time. Contact support for more information.';
        return this.createNotification({
            userId,
            type: NotificationType.UPGRADE_DENIED,
            title: 'Upgrade Request Declined',
            message,
            read: false,
            metadata: reason ? { reason } : {},
        });
    }

    // Helper: Warn user that their tier expires soon
    static async sendTierExpiryWarningNotification(
        userId: string,
        daysLeft: number
    ): Promise<string | null> {
        const dedupeKey = `tier_expiry_warning_${daysLeft}d_${userId}`;
        const exists = await this.notificationExists(userId, dedupeKey);
        if (exists) return null;
        return this.createNotification({
            userId,
            type: NotificationType.TIER_EXPIRING_SOON,
            title: `Your plan expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`,
            message: `Your paid subscription will expire in ${daysLeft} day${daysLeft === 1 ? '' : 's'}. Renew to keep your premium features.`,
            read: false,
            actionUrl: '/settings',
            dedupeKey,
            metadata: { daysLeft },
        });
    }

    // Helper: Notify user their tier expired and they were downgraded
    static async sendTierExpiredNotification(
        userId: string,
        previousTierName: string
    ): Promise<string> {
        return this.createNotification({
            userId,
            type: NotificationType.TIER_EXPIRED,
            title: `Your ${previousTierName} plan has expired`,
            message: `Your subscription has expired and your account has been moved to the Free plan. Upgrade anytime to restore premium access.`,
            read: false,
            actionUrl: '/settings',
            metadata: { previousTierName },
        });
    }

    // Helper: Notify user their account was blocked
    static async sendUserBlockedNotification(
        userId: string,
        reason?: string
    ): Promise<string> {
        const message = reason
            ? `Your account has been suspended. Reason: ${reason}. Please contact support.`
            : 'Your account has been suspended. Please contact support for assistance.';

        // Write to users/{uid}/notifications subcollection so Flutter mobile
        // clients (which read that path) also receive this notification.
        const subcollectionId = `user_blocked_${Date.now()}`;
        await setDoc(
            doc(db, 'users', userId, 'notifications', subcollectionId),
            {
                id: subcollectionId,
                type: 10, // NotificationType.userBlocked index in Flutter
                title: 'Account Suspended',
                body: message,
                timestamp: Date.now(),
                isRead: false,
                dismissed: false,
                data: reason ? { reason } : {},
                actionData: {},
            }
        ).catch(() => { /* non-fatal */ });

        return this.createNotification({
            userId,
            type: NotificationType.USER_BLOCKED,
            title: 'Account Suspended',
            message,
            read: false,
            metadata: reason ? { reason } : {},
        });
    }

    // ── Phase 8: Subscription payment notification helpers ─────────────────────
    // These are used when the web layer needs to write a notification directly
    // (e.g. optimistic UI, admin overrides, test flows).  For normal payment
    // flows the Cloud Function writes to `users/{uid}/notifications` and the
    // web reads it via `subscribeToSubscriptionNotifications()`.

    /** RC INITIAL_PURCHASE (non-trial): subscription is now active. */
    static async sendSubscriptionActivatedNotification(
        userId: string,
        tierName: string,
        source: string
    ): Promise<string> {
        return this.createNotification({
            userId,
            type: NotificationType.SUBSCRIPTION_ACTIVATED,
            title: `Welcome to TravelEZ ${tierName}! 🎉`,
            message: `Your ${tierName} subscription is now active. Enjoy all features!`,
            read: false,
            actionUrl: '/settings?tab=subscription',
            metadata: { tierName, source },
        });
    }

    /** RC RENEWAL: payment received and subscription renewed. */
    static async sendPaymentReceivedNotification(
        userId: string,
        tierName: string,
        options?: { invoiceUrl?: string; renewsUntil?: Date }
    ): Promise<string> {
        const renewsLabel = options?.renewsUntil
            ? ` Renews until ${options.renewsUntil.toLocaleDateString()}.`
            : '';
        const invoiceNote = options?.invoiceUrl
            ? ' Invoice available in your subscription screen.'
            : '';
        return this.createNotification({
            userId,
            type: NotificationType.PAYMENT_RECEIVED,
            title: 'Subscription renewed ✅',
            message: `Payment received.${renewsLabel}${invoiceNote}`,
            read: false,
            actionUrl: '/settings?tab=subscription',
            invoiceUrl: options?.invoiceUrl,
            metadata: { tierName },
        });
    }

    /** RC BILLING_ISSUE: payment failed or is past due. */
    static async sendPaymentFailedNotification(
        userId: string,
        isPaddle = false
    ): Promise<string> {
        const message = isPaddle
            ? "We couldn't process your payment. Please update your payment method."
            : "There's an issue with your payment. Access continues while we retry.";
        return this.createNotification({
            userId,
            type: NotificationType.PAYMENT_FAILED,
            title: 'Payment issue ⚠️',
            message,
            read: false,
            actionUrl: '/settings?tab=subscription&action=manage',
        });
    }

    /** RC CANCELLATION: subscription will cancel at period end. */
    static async sendSubscriptionCancelledNotification(
        userId: string,
        tierName: string,
        expiresAt?: Date
    ): Promise<string> {
        const expiryLabel = expiresAt
            ? expiresAt.toLocaleDateString()
            : 'the end of the billing period';
        return this.createNotification({
            userId,
            type: NotificationType.SUBSCRIPTION_CANCELLED,
            title: 'Subscription canceled',
            message: `Your ${tierName} plan will end on ${expiryLabel}. You can reactivate any time.`,
            read: false,
            actionUrl: '/settings?tab=subscription&action=manage',
            metadata: { tierName },
        });
    }

    /** RC UNCANCELLATION: subscription reactivated. */
    static async sendSubscriptionReactivatedNotification(
        userId: string,
        tierName: string,
        renewsAt?: Date
    ): Promise<string> {
        const renewsLabel = renewsAt ? ` Renews on ${renewsAt.toLocaleDateString()}.` : '';
        return this.createNotification({
            userId,
            type: NotificationType.SUBSCRIPTION_REACTIVATED,
            title: 'Subscription reactivated 🎉',
            message: `Your ${tierName} plan is back on!${renewsLabel}`,
            read: false,
            actionUrl: '/settings?tab=subscription',
            metadata: { tierName },
        });
    }

    /** RC SUBSCRIPTION_PAUSED (Google Play): subscription is paused. */
    static async sendSubscriptionPausedNotification(
        userId: string,
        tierName: string
    ): Promise<string> {
        return this.createNotification({
            userId,
            type: NotificationType.SUBSCRIPTION_PAUSED,
            title: 'Subscription paused',
            message: `Your ${tierName} subscription is paused (Google Play). Resume any time.`,
            read: false,
            actionUrl: '/settings?tab=subscription&action=manage',
            metadata: { tierName },
        });
    }

    /** RC NON_SUBSCRIPTION_PURCHASE: consumable quota credits added. */
    static async sendCreditsAddedNotification(
        userId: string,
        creditsDescription: string,
        options?: { invoiceUrl?: string }
    ): Promise<string> {
        const invoiceNote = options?.invoiceUrl
            ? ' Invoice available in your subscription screen.'
            : '';
        return this.createNotification({
            userId,
            type: NotificationType.CREDITS_ADDED,
            title: 'Credits added ✅',
            message: `Your credits have been topped up: ${creditsDescription}.${invoiceNote}`,
            read: false,
            actionUrl: '/settings?tab=subscription',
            invoiceUrl: options?.invoiceUrl,
        });
    }

    /** RC INITIAL_PURCHASE with period_type=TRIAL: trial started. */
    static async sendTrialStartedNotification(
        userId: string,
        tierName: string,
        trialEndsAt?: Date
    ): Promise<string> {
        const endsLabel = trialEndsAt ? ` on ${trialEndsAt.toLocaleDateString()}` : '';
        return this.createNotification({
            userId,
            type: NotificationType.TRIAL_STARTED,
            title: `Your ${tierName} trial has started! 🎉`,
            message: `You have 7 days free. Your trial ends${endsLabel}. We'll remind you before it ends.`,
            read: false,
            actionUrl: '/settings?tab=subscription',
            metadata: { tierName },
        });
    }

    /** checkTrialReminders day-6 warning: trial ending soon. */
    static async sendTrialEndingNotification(
        userId: string,
        tierName: string,
        trialEndsAt?: Date
    ): Promise<string> {
        const endsLabel = trialEndsAt ? trialEndsAt.toLocaleDateString() : 'tomorrow';
        return this.createNotification({
            userId,
            type: NotificationType.TRIAL_ENDING,
            title: `Your ${tierName} trial ends soon ⏳`,
            message: `Your free trial ends on ${endsLabel}. You'll be charged automatically unless you cancel.`,
            read: false,
            actionUrl: '/settings?tab=subscription&action=manage',
            metadata: { tierName },
        });
    }
}

export default NotificationService;
